import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { hooks as h } from "@/hooks";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { uniqueOrganisationsFromRoles } from "@/features/auth/utils/uniqueOrganisationsFromRoles";
import { useSelectSurgeryOptions } from "@/features/events/features/surgeries/api/selectSurgeryOptions";
import { useSelectForms } from "@/features/forms/api/selectForms";
import { useInjectionMethodsQuery } from "@/features/injection/api/selectInjectionMethods";
import Stepper from "@/components/common/Stepper";
import {
    type DiaryInviteSearchT,
    getDiaryInviteContext,
    getDiaryInviteOrigin,
} from "@/features/invite/utils/diaryInviteContext";
import {
    hasDiaryInvitePayload,
    readDiaryInviteDraft,
    saveDiaryInviteDraft,
} from "@/features/invite/utils/diaryInviteDraft";
import { clearInviteContactExplicit } from "@/features/invite/utils/inviteContactChoice";
import { getDiaryInviteSteps, parseDiaryInviteMethod } from "@/features/invite/utils/diaryInviteSteps";
import { useSelectStaff } from "@/features/profile/api/selectStaff";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import DiaryInviteReviewForm from "@/features/templates/components/diary-invite/DiaryInviteReviewForm";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";

import type { TemplateSettingsSchemaT } from "@patient-watch/types";

export const Route = createFileRoute("/_app/invite/diary/review")({
    component: DiaryInviteReviewRoute,
    validateSearch: (search: Record<string, unknown>) => search as DiaryInviteSearchT,
});

function DiaryInviteReviewRoute() {
    const routeSearch = Route.useSearch();
    const navigate = useNavigate();
    const inviteCopy = useInviteCopy();
    const patientsCopy = usePatientsCopy();
    const templatesCopy = useTemplatesCopy();

    const search = useMemo(() => {
        if (hasDiaryInvitePayload(routeSearch)) return routeSearch;
        return readDiaryInviteDraft() ?? routeSearch;
    }, [routeSearch]);

    const origin = getDiaryInviteOrigin(search);
    const inviteMethod = parseDiaryInviteMethod(search.invite_method);
    const context = getDiaryInviteContext(origin, {
        patients: patientsCopy.patients,
        templates: templatesCopy.templates,
        newDiary: patientsCopy.newDiary,
    });
    const crumbSearch = { origin };

    useEffect(() => {
        if (!hasDiaryInvitePayload(routeSearch)) return;
        // A fresh template invite has no contact yet, so the default contact person can be applied.
        // Coming back from a later step still carries the contact the clinician chose.
        if (!routeSearch.contact_id) clearInviteContactExplicit();
        saveDiaryInviteDraft(routeSearch);
        void navigate({
            to: "/invite/diary/review",
            search: () => ({ origin }),
            replace: true,
            resetScroll: false,
        });
    }, [routeSearch, navigate, origin]);

    h.useNav([
        { label: context.rootLabel, route: { to: context.rootTo } },
        { label: context.flowLabel, route: { to: "/invite/diary/review", search: crumbSearch } },
        { label: inviteCopy.reviewSetup, route: { to: "/invite/diary/review", search: crumbSearch } },
    ]);

    const { auth } = Route.useRouteContext();
    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;
    const { data: userOrgRoles = [], isLoading: isLoadingOrgs } = useUsersOrganisationsRolesQuery(auth.id);

    const { data: staffData = [] } = useSelectStaff();
    const { data: formsData = [] } = useSelectForms();
    const { data: schedulesData = [] } = useSchedulesQuery();
    const { data: injectionMethods = [] } = useInjectionMethodsQuery();
    const { data: surgeryOptions = [] } = useSelectSurgeryOptions();

    const organisations = useMemo(() => uniqueOrganisationsFromRoles(userOrgRoles), [userOrgRoles]);

    return (
        <div className="space-y-4">
            {!isLoadingOrgs ? (
                <Stepper
                    steps={getDiaryInviteSteps("review", {
                        organisationCount: organisations.length,
                        inviteMethod,
                        copy: inviteCopy,
                    })}
                />
            ) : null}

            <DiaryInviteReviewForm
                search={search as TemplateSettingsSchemaT}
                organisations={organisations}
                staff={staffData as any}
                forms={formsData as any}
                schedules={schedulesData as any}
                injectionMethods={injectionMethods}
                surgeryOptions={surgeryOptions}
                isAdmin={isAdmin}
            />
        </div>
    );
}
